// @flow
import * as React from 'react';
import { get } from 'lodash';
import { Formik } from 'formik';
import type { ApiClient } from 'speed-js-core';
import connectAPI from 'speed-js-react/src/api/connectAPI';
import GrommetForm from 'speed-js-react-web/src/components/grommet2/forms/GrommetForm';
import Spinner from 'esm-web-pro/components/forms/Spinner';
import { FormattedMessage } from 'react-intl';
import { Text } from 'grommet';

type Props = {
    forwardRef: React.ElementRef<Formik>,
    schema: Object,
    initial: Object,
    onChange: Function,
    onInitialize: Function,
    actions?: Object,
    // eslint-disable-next-line react/no-unused-prop-types
    excludeFields?: Array<string>,
    schemaError?: Object,
    height: ?string,
    padding: ?string,
    overflow: ?string,
}

type CombinedProps = Props & {
    getSchema: ?() => Promise<Object>,
};

@connectAPI((apiClient: ApiClient, props: CombinedProps) => ({
    getSchema: {
        send: () => {
            const resource = get(apiClient.resources, props.resourceName);
            if (props.resourceArguments) {
                return resource.schema(...props.resourceArguments);
            }
            return resource.schema();
        },
        success: (schema: Object) => {
            if (props.excludeFields) {
                schema.fields = schema.fields.filter((value) => props.excludeFields.indexOf(value[0]) === -1);
            }

            const initial = {};

            schema.fields.forEach((schemaField) => {
                const [name, value] = schemaField;
                const initialValue = schema.fields.find((field) => field[0] === name)[1].initial;
                if (initialValue !== undefined && initialValue !== null) {
                    initial[name] = initialValue;
                }
            });

            const initialData = props.initial ? { initial: { ...initial, ...props.initial } } : { initial };

            return { schema, ...initialData };
        },
        error: (response) => ({ schemaError: response }),
    },
    getInitial: {
        send: () => get(apiClient.resources, props.resourceName).get(
            ...props.resourceArguments, props.objectId, { nonested: 1 },
        ),
        success: (initial: Object) => (props.initial ? { initial: { ...initial, ...props.initial } } : { initial }),
    },
}))
class MapObjectForm extends React.Component<CombinedProps> {
    static defaultProps = {
        getSchema: undefined,
        initial: null,
        schema: null,
        actions: null,
        excludeFields: undefined,
        schemaError: null,
        height: null,
        padding: null,
        overflow: null,
    };

    componentDidMount() {
        const { getSchema, onInitialize } = this.props;
        if (getSchema) {
            getSchema();
        }
        if (onInitialize) {
            onInitialize();
        }
    }

    componentDidUpdate(prevProps: Props) {
        const { onInitialize, schema } = this.props;

        if (onInitialize && schema !== prevProps.schema) {
            onInitialize();
        }
    }

    render() {
        const {
            schema, initial, forwardRef, onChange, actions, schemaError, height, padding, overflow,
        } = this.props;

        if (!schema && !schemaError) {
            return (
                <Spinner
                    type="clipLoader"
                    loading
                    blocking={false}
                    color="black"
                    coverStyle={{
                        position: 'relative',
                        top: '20px',
                    }}
                />
            );
        }

        if (!schema && schemaError) {
            return (
                <Text color="status-error" margin={{ top: 'small' }}>
                    <FormattedMessage
                        id="mapobjectform-schema-error"
                        defaultMessage="The form could not be loaded due to an error."
                    />
                </Text>
            );
        }
        return (
            <GrommetForm
                ref={forwardRef}
                schema={schema}
                initial={initial}
                nonFieldErrorsName="non_field_errors"
                height={height}
                overflow={overflow}
                padding={padding}
                onChange={onChange}
                actions={actions}
                validateOnChange
            />
        );
    }
}

export default MapObjectForm;
